<template>
	<div class="relative mt-4">
		<PageTitle>{{ t('title') }}</PageTitle>

		<PagePadding class="mt-4">
			<InfoAlert
				id="verification-info"
				:title="t('info.title')"
				:desc="t('info.desc')"
			/>

			<div class="mt-4">
				<div class="text-label-secondary mb-3 text-xs font-medium uppercase">
					{{ t('selectGame') }}
				</div>

				<div class="flex gap-2">
					<BaseCard
						alt
						class="flex flex-1 items-center gap-3 p-4"
						v-hover="{ active: 'scale-95', impact: 'soft' }"
						@click="setGameType('case')"
					>
						<BaseRadio v-model="isCase" />
						<div class="text-label-primary text-sm font-medium">
							{{ t('caseGame') }}
						</div>
					</BaseCard>

					<BaseCard
						alt
						class="flex flex-1 items-center gap-3 p-4"
						v-hover="{ active: 'scale-95', impact: 'soft' }"
						@click="setGameType('battle')"
					>
						<BaseRadio v-model="isBattle" />
						<div class="text-label-primary text-sm font-medium">
							{{ t('battleGame') }}
						</div>
					</BaseCard>
				</div>
			</div>

			<div
				class="mt-6"
				:class="{ 'pointer-events-none opacity-50': !gameType }"
			>
				<div class="text-label-secondary mb-3 text-xs font-medium uppercase">
					{{ t('gameData') }}
				</div>

				<BaseCard alt class="p-4">
					<textarea
						v-model="rawData"
						:placeholder="t('inputPlaceholder')"
						class="text-label-primary placeholder-label-secondary w-full resize-none bg-transparent text-sm focus:outline-none"
						rows="8"
						spellcheck="false"
					/>
				</BaseCard>

				<div class="mt-3 flex gap-2">
					<BaseButton
						size="sm"
						color="secondary"
						class="flex-1"
						@click="fillSampleData"
						:disabled="!gameType"
					>
						{{ t('fillSample') }}
					</BaseButton>
					<BaseButton
						size="sm"
						color="secondary"
						class="flex-1"
						@click="clearData"
					>
						{{ t('clear') }}
					</BaseButton>
				</div>
			</div>

			<BaseButton
				class="mt-6 w-full"
				size="lg"
				color="primary"
				@click="verifyGame"
				:disabled="!canVerify"
				:loading="verifying"
			>
				<div>
					{{ t('verify') }}
				</div>
			</BaseButton>

			<!-- Verification Results -->
			<div v-if="verificationResult" class="mt-6">
				<div class="text-label-secondary mb-3 text-xs font-medium uppercase">
					{{ t('verificationResults') }}
				</div>

				<BaseCard class="p-4">
					<div
						class="mb-4 flex items-center gap-2"
						:class="{
							'text-green': verificationResult.isValid,
							'text-red': !verificationResult.isValid
						}"
					>
						<CheckIcon v-if="verificationResult.isValid" class="h-5 w-5" />
						<CloseIcon v-else class="h-5 w-5" />
						<div class="font-semibold">
							{{
								verificationResult.isValid
									? t('verificationPassed')
									: t('verificationFailed')
							}}
						</div>
					</div>

					<div class="space-y-3">
						<div
							v-for="result in verificationResult.results"
							:key="result.id"
							class="border-separator-primary border-t pt-3 first:border-t-0 first:pt-0"
						>
							<div class="text-label-primary mb-2 text-sm font-medium">
								{{ result.label }}
							</div>
							<div class="space-y-2">
								<div
									v-for="check in result.checks"
									:key="check.name"
									class="flex items-center justify-between text-xs"
								>
									<div class="text-label-secondary">{{ check.name }}</div>
									<div
										class="flex items-center gap-1"
										:class="{
											'text-green': check.passed,
											'text-red': !check.passed
										}"
									>
										<CheckIcon v-if="check.passed" class="h-3 w-3" />
										<CloseIcon v-else class="h-3 w-3" />
										<span>{{ check.passed ? t('pass') : t('fail') }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					<BaseButton
						class="mt-4 w-full"
						size="sm"
						color="secondary"
						@click="copyVerificationData"
						:loading="copying"
					>
						{{ t('copyResults') }}
					</BaseButton>
				</BaseCard>
			</div>
		</PagePadding>
	</div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import PageTitle from '@/components/PageTitle.vue'
import PagePadding from '@/components/PagePadding.vue'
import InfoAlert from '@/components/InfoAlert.vue'
import BaseCard from '@/components/BaseCard.vue'
import BaseRadio from '@/components/BaseRadio.vue'
import BaseButton from '@/components/BaseButton.vue'
import CheckIcon from '@/assets/icons/check.svg'
import CloseIcon from '@/assets/icons/close.svg'
import { useToast } from '@/stores/toast'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({
	messages: {
		en: {
			title: 'Game Verification',
			info: {
				title: 'Verification System',
				desc: 'Verify the fairness of battles and case openings using cryptographic proofs.'
			},
			selectGame: 'Select Game Type',
			caseGame: 'Case',
			battleGame: 'Battle',
			gameData: 'Game Data',
			inputPlaceholder: 'Paste your game data JSON here...',
			fillSample: 'Fill Sample',
			clear: 'Clear',
			verify: 'Verify Game',
			verificationResults: 'Verification Results',
			verificationPassed: 'Verification Passed',
			verificationFailed: 'Verification Failed',
			pass: 'Pass',
			fail: 'Fail',
			copyResults: 'Copy Results',
			copied: 'Copied to clipboard',
			invalidJson: 'Invalid JSON data',
			missingData: 'Missing required data fields',
			serverSeedHash: 'Server Seed Hash',
			generatedTicket: 'Generated Ticket',
			actualTicket: 'Actual Ticket',
			ticketMatch: 'Ticket Match',
			hashVerification: 'Hash Verification',
			roundVerification: 'Round {round} Verification',
			participantVerification: 'Participant {participant}',
			battleDataStructure: 'Battle Data Structure',
			caseDataStructure: 'Case Data Structure',
			serverSeedPresent: 'Server Seed Present',
			ticketRangeValid: 'Ticket Range Valid'
		},
		ru: {
			title: 'Проверка Игр',
			info: {
				title: 'Система Проверки',
				desc: 'Проверьте честность батлов и открытий кейсов с помощью криптографических доказательств.'
			},
			selectGame: 'Выберите Тип Игры',
			caseGame: 'Кейс',
			battleGame: 'Батл',
			gameData: 'Данные Игры',
			inputPlaceholder: 'Вставьте JSON данные игры здесь...',
			fillSample: 'Заполнить Пример',
			clear: 'Очистить',
			verify: 'Проверить Игру',
			verificationResults: 'Результаты Проверки',
			verificationPassed: 'Проверка Пройдена',
			verificationFailed: 'Проверка Не Пройдена',
			pass: 'Пройдено',
			fail: 'Не пройдено',
			copyResults: 'Копировать Результаты',
			copied: 'Скопировано',
			invalidJson: 'Неверные JSON данные',
			missingData: 'Отсутствуют обязательные поля данных',
			serverSeedHash: 'Хеш Сервер Seed',
			generatedTicket: 'Сгенерированный Билет',
			actualTicket: 'Фактический Билет',
			ticketMatch: 'Совпадение Билета',
			hashVerification: 'Проверка Хеша',
			roundVerification: 'Проверка Раунда {round}',
			participantVerification: 'Участник {participant}',
			battleDataStructure: 'Структура Данных Батла',
			caseDataStructure: 'Структура Данных Кейса',
			serverSeedPresent: 'Наличие Server Seed',
			ticketRangeValid: 'Валидный Диапазон Билетов'
		}
	}
})

const toast = useToast()

// Game type selection
const gameType = ref<'case' | 'battle' | null>(null)
const isCase = computed({
	get: () => gameType.value === 'case',
	set: (value) => {
		if (value) gameType.value = 'case'
	}
})
const isBattle = computed({
	get: () => gameType.value === 'battle',
	set: (value) => {
		if (value) gameType.value = 'battle'
	}
})

const setGameType = (type: 'case' | 'battle') => {
	gameType.value = type
}

// Data management
const rawData = ref('')
const verifying = ref(false)
const copying = ref(false)

const canVerify = computed(() => {
	return gameType.value && rawData.value.trim().length > 0 && !verifying.value
})

// Verification results
const verificationResult = ref<{
	isValid: boolean
	results: {
		id: string
		label: string
		checks: {
			name: string
			passed: boolean
			details?: any
		}[]
	}[]
} | null>(null)

// Sample data for testing
const sampleCaseData = {
	nonce: [19],
	serverSeedHash:
		'531cb0ea30d126a56e9839fced1abd701ac2f86e1d35c1dbde2a809a40808f77',
	serverSeed:
		'OKTamhhlPSAGWSiX5KnAWxmMelZWpVHmQDH4zpv1yXlfAivwS12yXLNaEwiBNyoY',
	clientSeed: 'hWNqa',
	ticketsCount: 10000000,
	ticket: [6596756],
	uuid: '019a0239-d774-862d-82dd-5355bf03feef'
}

const sampleBattleData = {
	uuid: '019a0bc9-7152-8d91-b0e6-80dbaaf797da',
	fair: {
		serverSeed:
			'ePP58QAk5Qao4UgEKm3kxkGxMQT52sCnVJs23NnpMi9myesBzB6lT9b6Pit1pyJ9',
		serverSeedHash:
			'16a4d01bc02418d076e66d49b6050d5b1d8d9dcd851827429ef70b246644ee1b',
		tonBlockNumber: 53218908,
		tonBlockHash:
			'67710889a1fa9e57d06a327dbe03e45a373f487cf6587c834a4a895ab83a3755'
	},
	rounds: [
		{
			results: [
				{
					participant: {
						slotIndex: 0,
						teamIndex: 0,
						name: 'Dmitry'
					},
					item: {
						modelUuid: '01982e20-2cd9-73a7-9778-05264d8a4569',
						ticket: 538873
					}
				},
				{
					participant: {
						slotIndex: 0,
						teamIndex: 1,
						name: 'Ms. Drop'
					},
					item: {
						modelUuid: '01982e20-2cd9-73a7-9778-05264d8a4569',
						ticket: 8434164
					}
				}
			]
		},
		{
			results: [
				{
					participant: {
						slotIndex: 0,
						teamIndex: 0,
						name: 'Dmitry'
					},
					item: {
						modelUuid: '01982e20-2cd9-73a7-9778-05264d8a4569',
						ticket: 3453996
					}
				},
				{
					participant: {
						slotIndex: 0,
						teamIndex: 1,
						name: 'Ms. Drop'
					},
					item: {
						modelUuid: '01982e20-2cd9-73a7-9778-05264d8a4569',
						ticket: 1847772
					}
				}
			]
		}
	],
	cases: [
		{
			name: 'test',
			items: [
				{
					modelUuid: '01982e20-2cd9-73a7-9778-05264d8a4569',
					ticketsRange: [0, 9899999],
					chance: 99
				},
				{
					modelUuid: '01983f0d-b6bd-7194-88b2-eca1c4a79cb1',
					ticketsRange: [9900000, 9999999],
					chance: 1
				}
			]
		},
		{
			name: 'test',
			items: [
				{
					modelUuid: '01982e20-2cd9-73a7-9778-05264d8a4569',
					ticketsRange: [0, 9899999],
					chance: 99
				},
				{
					modelUuid: '01983f0d-b6bd-7194-88b2-eca1c4a79cb1',
					ticketsRange: [9900000, 9999999],
					chance: 1
				}
			]
		}
	]
}

// Data management functions
const fillSampleData = () => {
	if (gameType.value === 'case') {
		rawData.value = JSON.stringify(sampleCaseData, null, 2)
	} else if (gameType.value === 'battle') {
		rawData.value = JSON.stringify(sampleBattleData, null, 2)
	}
}

const clearData = () => {
	rawData.value = ''
	verificationResult.value = null
}

// Type definitions for verification functions
type ConvertSeedToFloatParams = {
	serverSeed: string
	clientSeed: string
	nonce: string
}

type GetWinningTicketForBattleRoundParams = {
	battleUuid: string
	round: number
	slot: number
	tickets: number
	serverSeed: string
	tonBlockHash: string
}

type GetTieBreakWinnerParams = {
	tonBlockHash: string
	serverSeed: string
	battleUuid: string
	userCount: number
}

type GetWinningTicketForCaseOpeningParams = {
	serverSeed: string
	clientSeed: string
	nonce: string
	tickets: number
	userUuid: string
}

// function convertSeedToFloat({
// 	serverSeed,
// 	clientSeed,
// 	nonce,
// }: ConvertSeedToFloatParams) {
// 	const hash = new Bun.CryptoHasher('sha256', serverSeed)
// 		.update(`${clientSeed}:${nonce}`)
// 		.digest('hex')

// 	const significant = hash.substring(0, 13)
// 	const integer = Number.parseInt(significant, 16)
// 	const float = integer / 2 ** 52

// 	return {
// 		float,
// 		hash,
// 	}
// }

async function hmacSHA256Hex(
	serverSeed: string,
	clientSeed: string,
	nonce: string
) {
	const keyData = new TextEncoder().encode(serverSeed)
	const inputData = new TextEncoder().encode(`${clientSeed}:${nonce}`)
	// Import the key for HMAC
	const cryptoKey = await crypto.subtle.importKey(
		'raw',
		keyData,
		{ name: 'HMAC', hash: 'SHA-256' },
		false,
		['sign', 'verify']
	)
	// Perform HMAC
	const signature = await crypto.subtle.sign('HMAC', cryptoKey, inputData)
	// Convert ArrayBuffer result to hex
	return Array.from(new Uint8Array(signature))
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('')
}

// WebCrypto API replacement for Bun.CryptoHasher
async function convertSeedToFloat({
	serverSeed,
	clientSeed,
	nonce
}: ConvertSeedToFloatParams) {
	const hash = await hmacSHA256Hex(serverSeed, clientSeed, nonce)

	// Take first 13 hex digits and convert to float
	const significant = hash.substring(0, 13)
	const integer = Number.parseInt(significant, 16)
	const float = integer / 2 ** 52

	return float
}

async function getWinningTicketForBattleRound({
	battleUuid,
	round,
	slot,
	tickets,
	serverSeed,
	tonBlockHash
}: GetWinningTicketForBattleRoundParams) {
	const float = await convertSeedToFloat({
		clientSeed: tonBlockHash,
		serverSeed,
		nonce: `${battleUuid}:${round}:${slot}`
	})

	return Math.floor(float * tickets)
}

async function getTieBreakWinner({
	battleUuid,
	tonBlockHash,
	serverSeed,
	userCount
}: GetTieBreakWinnerParams) {
	const float = await convertSeedToFloat({
		clientSeed: tonBlockHash,
		serverSeed,
		nonce: battleUuid
	})

	return Math.floor(float * userCount)
}

async function getWinningTicketForCaseOpening({
	serverSeed,
	clientSeed,
	nonce,
	tickets,
	userUuid
}: GetWinningTicketForCaseOpeningParams) {
	const float = await convertSeedToFloat({
		clientSeed,
		serverSeed,
		nonce: `${nonce}:${userUuid}`
	})

	return Math.floor(float * tickets)
}

// Server seed hash verification using WebCrypto
async function verifyServerSeedHash(
	serverSeed: string,
	expectedHash: string
): Promise<boolean> {
	if (!serverSeed) {
		return false
	}

	try {
		const encoder = new TextEncoder()
		const data = encoder.encode(serverSeed)
		const hashBuffer = await crypto.subtle.digest('SHA-256', data)
		const hashArray = new Uint8Array(hashBuffer)
		const hashHex = Array.from(hashArray)
			.map((b) => b.toString(16).padStart(2, '0'))
			.join('')

		return hashHex.toLowerCase() === expectedHash.toLowerCase()
	} catch {
		return false
	}
}

// Main verification functions
const verifyGame = async () => {
	if (!canVerify.value) {
		return
	}

	verifying.value = true
	verificationResult.value = null

	try {
		const data = JSON.parse(rawData.value.trim())

		if (gameType.value === 'case') {
			await verifyCaseGame(data)
		} else if (gameType.value === 'battle') {
			await verifyBattleGame(data)
		}
	} catch (error) {
		toast.add({
			type: 'error',
			text: t('invalidJson')
		})
	} finally {
		verifying.value = false
	}
}

const verifyCaseGame = async (data: any) => {
	const results = []
	let overallValid = true

	// Validate data structure
	const structureChecks = []
	const hasRequiredFields =
		data.serverSeedHash &&
		data.clientSeed &&
		data.nonce &&
		data.ticketsCount &&
		data.uuid
	structureChecks.push({
		name: t('caseDataStructure'),
		passed: hasRequiredFields
	})

	if (data.serverSeed) {
		const hashValid = await verifyServerSeedHash(
			data.serverSeed,
			data.serverSeedHash
		)
		structureChecks.push({
			name: t('hashVerification'),
			passed: hashValid
		})
		if (!hashValid) overallValid = false
	} else {
		structureChecks.push({
			name: t('serverSeedPresent'),
			passed: false
		})

		overallValid = false
	}

	results.push({
		id: 'structure',
		label: t('caseDataStructure'),
		checks: structureChecks
	})

	// Verify each ticket if serverSeed is available
	if (data.serverSeed && data.ticket) {
		for (let i = 0; i < data.nonce.length; i++) {
			const ticketChecks = []

			try {
				const generatedTicket = await getWinningTicketForCaseOpening({
					serverSeed: data.serverSeed,
					clientSeed: data.clientSeed,
					nonce: data.nonce[i].toString(),
					tickets: data.ticketsCount,
					userUuid: data.uuid
				})

				const actualTicket = data.ticket[i]
				const ticketMatch = generatedTicket === actualTicket

				ticketChecks.push({
					name: `${t('generatedTicket')}: ${generatedTicket}`,
					passed: true
				})
				ticketChecks.push({
					name: `${t('actualTicket')}: ${actualTicket}`,
					passed: true
				})
				ticketChecks.push({
					name: t('ticketMatch'),
					passed: ticketMatch
				})

				if (!ticketMatch) overallValid = false
			} catch (error) {
				ticketChecks.push({
					name: 'Verification Error',
					passed: false
				})
				overallValid = false
			}

			results.push({
				id: `ticket-${i}`,
				label: `Ticket ${i + 1} (Nonce: ${data.nonce[i]})`,
				checks: ticketChecks
			})
		}
	}

	if (!hasRequiredFields) overallValid = false

	verificationResult.value = {
		isValid: overallValid,
		results
	}
}

const verifyBattleGame = async (data: any) => {
	const results = []
	let overallValid = true

	// Validate data structure
	const structureChecks = []
	const hasRequiredFields = data.uuid && data.fair && data.rounds && data.cases
	structureChecks.push({
		name: t('battleDataStructure'),
		passed: hasRequiredFields
	})

	if (data.fair?.serverSeed && data.fair?.serverSeedHash) {
		const hashValid = await verifyServerSeedHash(
			data.fair.serverSeed,
			data.fair.serverSeedHash
		)
		structureChecks.push({
			name: t('hashVerification'),
			passed: hashValid
		})
		if (!hashValid) overallValid = false
	} else {
		structureChecks.push({
			name: t('serverSeedPresent'),
			passed: !!data.fair?.serverSeed
		})
	}

	results.push({
		id: 'structure',
		label: t('battleDataStructure'),
		checks: structureChecks
	})

	// Verify each round if serverSeed is available
	if (data.fair?.serverSeed && data.fair?.tonBlockHash) {
		for (let roundIndex = 0; roundIndex < data.rounds.length; roundIndex++) {
			const round = data.rounds[roundIndex]

			for (
				let resultIndex = 0;
				resultIndex < round.results.length;
				resultIndex++
			) {
				const result = round.results[resultIndex]
				const roundChecks = []

				try {
					// Get the maximum ticket value from the case items for this round
					const caseItems = data.cases[roundIndex]?.items || []
					const maxTickets =
						Math.max(...caseItems.map((item: any) => item.ticketsRange[1])) + 1

					const generatedTicket = await getWinningTicketForBattleRound({
						battleUuid: data.uuid,
						round: roundIndex + 1,
						slot: resultIndex + 1,
						tickets: maxTickets,
						serverSeed: data.fair.serverSeed,
						tonBlockHash: data.fair.tonBlockHash
					})

					const actualTicket = result.item.ticket
					const ticketMatch = generatedTicket === actualTicket

					roundChecks.push({
						name: `${t('generatedTicket')}: ${generatedTicket}`,
						passed: true
					})
					roundChecks.push({
						name: `${t('actualTicket')}: ${actualTicket}`,
						passed: true
					})
					roundChecks.push({
						name: t('ticketMatch'),
						passed: ticketMatch
					})

					// Verify ticket is in valid range for the item
					const matchingItem = caseItems.find(
						(item: any) =>
							actualTicket >= item.ticketsRange[0] &&
							actualTicket <= item.ticketsRange[1]
					)

					roundChecks.push({
						name: t('ticketRangeValid'),
						passed:
							!!matchingItem && matchingItem.modelUuid === result.item.modelUuid
					})

					if (!ticketMatch) overallValid = false
				} catch (error) {
					roundChecks.push({
						name: 'Verification Error',
						passed: false
					})
					overallValid = false
				}

				results.push({
					id: `round-${roundIndex}-${resultIndex}`,
					label: `${t('roundVerification', { round: roundIndex + 1 })} - ${result.participant.name}`,
					checks: roundChecks
				})
			}
		}
	}

	if (!hasRequiredFields) overallValid = false

	verificationResult.value = {
		isValid: overallValid,
		results
	}
}

const copyVerificationData = async () => {
	if (!verificationResult.value) return

	copying.value = true

	try {
		const resultText = JSON.stringify(verificationResult.value, null, 2)
		await navigator.clipboard.writeText(resultText)

		toast.add({
			type: 'success',
			text: t('copied')
		})
	} catch {
		// Fallback for unsupported browsers
	} finally {
		copying.value = false
	}
}
</script>
